import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { History, Users } from "lucide-react";
import { useForm } from "react-hook-form";

import { useCompany } from "@/lib/CompanyProvider";
import type { CollabFormValues } from "@/hooks/team/useTeamData";
import {
  useTeamData,
  collaboratorSchema } from "@/hooks/team/useTeamData";
import { InviteModal } from "@/components/team/InviteModal";
import { CollabFormModal } from "@/components/team/CollabFormModal";
import { PayCommissionModal } from "@/components/team/PayCommissionModal";
import { TeamGrid } from "@/components/team/TeamGrid";
import { AuditLogs } from "@/components/team/AuditLogs";

function TeamModule() {
  const { activeCompanyId } = useCompany();
  const [activeTab, setActiveTab] = useState<"equipe" | "auditoria">("equipe");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCollab, setEditingCollab] = useState<any>(null);

  // Invite states
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteRole, setInviteRole] = useState<
    "ADMIN" | "CAIXA" | "ATENDENTE" | "COLABORADOR"
  >("COLABORADOR");
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const {
    collaborators,
    teamOrders,
    auditLogs,
    isLoading,
    isLoadingLogs,
    saveCollab,
    toggleStatus,
    payCommission,
    generateInvite,
  } = useTeamData(activeCompanyId, activeTab);

  // Form
  const form = useForm<CollabFormValues>({
    resolver: zodResolver(collaboratorSchema) as any,
    defaultValues: {
      name: "",
      phone: "",
      specialty: "Lavagem e Acabamento",
      system_role: "COLABORADOR",
      commission_rate: 10,
      status: "ATIVO",
    },
  });

  // Pay commission states
  const [payCollabId, setPayCollabId] = useState<string | null>(null);
  const [payAmount, setPayAmount] = useState<number>(0);
  const [payMethod, setPayMethod] = useState<string>("PIX");

  const collabToPay = collaborators.find((c: any) => c.id === payCollabId);

  const onSubmit = (data: CollabFormValues) => {
    saveCollab.mutate(
      { ...data, id: editingCollab?.id },
      {
        onSuccess: (_res: any, variables: any) => {
          setIsModalOpen(false);
          setEditingCollab(null);
          form.reset();

          if (
            !variables.id &&
            variables.system_role &&
            variables.system_role !== "COLABORADOR"
          ) {
            setInviteRole(variables.system_role as any);
            setInviteModalOpen(true);
            generateInvite.mutate({
              role: variables.system_role,
              company_id: activeCompanyId || undefined,
            });
          }
        },
      },
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-lg">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Users className="text-blue-500" size={32} />
            Gestão de Equipe
          </h1>
          <p className="text-slate-400 mt-1">
            Gerencie seus funcionários, comissões e histórico.
          </p>
        </div>

        <div className="flex bg-slate-950 p-1 rounded-xl">
          <button
            onClick={() => setActiveTab("equipe")}
            className={`px-6 py-2 rounded-lg font-semibold text-sm transition-colors ${
              activeTab === "equipe"
                ? "bg-slate-800 text-white shadow-sm"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            Membros da Equipe
          </button>
          <button
            onClick={() => setActiveTab("auditoria")}
            className={`px-6 py-2 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2 ${
              activeTab === "auditoria"
                ? "bg-slate-800 text-white shadow-sm"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <History size={16} /> Auditoria
          </button>
        </div>
      </div>

      {activeTab === "equipe" ? (
        <>
          <div className="flex justify-end gap-3">
            <InviteModal
              isOpen={inviteModalOpen}
              setIsOpen={setInviteModalOpen}
              inviteRole={inviteRole}
              setInviteRole={setInviteRole}
              generatedLink={generatedLink}
              setGeneratedLink={setGeneratedLink}
              copied={copied}
              setCopied={setCopied}
              generateInvite={generateInvite}
              activeCompanyId={activeCompanyId}
            />

            <CollabFormModal
              isOpen={isModalOpen}
              setIsOpen={setIsModalOpen}
              editingCollab={editingCollab}
              form={form}
              onSubmit={onSubmit}
              isPending={saveCollab.isPending}
            />
          </div>

          <PayCommissionModal
            payCollabId={payCollabId}
            setPayCollabId={setPayCollabId}
            collabToPay={collabToPay}
            payAmount={payAmount}
            setPayAmount={setPayAmount}
            payMethod={payMethod}
            setPayMethod={setPayMethod}
            payCommission={payCommission}
          />

          <TeamGrid
            collaborators={collaborators}
            teamOrders={teamOrders}
            isLoading={isLoading}
            setEditingCollab={setEditingCollab}
            form={form}
            setIsModalOpen={setIsModalOpen}
            toggleStatus={toggleStatus}
            setPayCollabId={setPayCollabId}
            setPayAmount={setPayAmount}
          />
        </>
      ) : (
        <AuditLogs logs={auditLogs} isLoading={isLoadingLogs} />
      )}
    </div>
  );
}

export const Route = createFileRoute("/app/team")({
  component: TeamModule,
});
